// Which mapping rows actually block posting, and in what order to show them.
//
// The banner used to say "3 required account(s) still need an account" and
// stop there, while the table sorted by source — which put all twenty payroll
// rows, including every required one, BELOW the commission and expense rows.
// The three that mattered sat at positions 20, 25 and 28 of 32.
//
// A tester read the banner, looked at the top of the table, and reported the
// first three rows as the blockers. They were not. Nothing on the screen was
// false; it was just arranged so that the obvious reading was wrong.

export interface MappingRowLike {
  logical_code: string;
  label?: string;
  required: boolean;
  mapped: boolean;
  problem?: string;
}

/** Rows that stop a journal posting: required, and either unmapped or
 *  pointing at something unusable. */
export const blockingRows = <T extends MappingRowLike>(rows: T[]): T[] =>
  rows.filter((r) => r.required && (!r.mapped || Boolean(r.problem)));

/** Blockers first, then the rest in the order the server sent them.
 *  A list whose first screenful excludes every item the warning is about is
 *  not a list, it is a hiding place. */
export const blockersFirst = <T extends MappingRowLike>(rows: T[]): T[] => {
  const blocking = new Set(blockingRows(rows).map((r) => r.logical_code));
  return [...rows].sort((a, b) => {
    const ab = blocking.has(a.logical_code) ? 0 : 1;
    const bb = blocking.has(b.logical_code) ? 0 : 1;
    return ab - bb; // Array.prototype.sort is stable, so ties keep server order
  });
};

/** The names to put in the warning — a count alone sends people hunting. */
export const blockingNames = (rows: MappingRowLike[], max = 3): string => {
  const names = blockingRows(rows).map((r) => r.label || r.logical_code);
  if (names.length === 0) return '';
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
};
